import { useEffect, useMemo, useState } from 'react'
import styles from './dualRangeSlider.module.scss'

export const DualRangeSlider = ({
    min,
    max,
    valueMin,
    valueMax,
    step,
    histogramValues = [],
    histogramOverlayValues = [],
    histogramScale = 'linear',
    onChangeMin,
    onChangeMax,
}: {
    min: number
    max: number
    valueMin: number
    valueMax: number
    step: number
    histogramValues?: number[]
    histogramOverlayValues?: number[]
    histogramScale?: 'linear' | 'log'
    onChangeMin: (v: number) => void
    onChangeMax: (v: number) => void
    formatValue: (v: number) => string
}) => {
    const [minText, setMinText] = useState(String(valueMin))
    const [maxText, setMaxText] = useState(String(valueMax))

    useEffect(() => setMinText(String(valueMin)), [valueMin])
    useEffect(() => setMaxText(String(valueMax)), [valueMax])

    const applyMin = () => {
        const v = parseFloat(minText)
        if (!isNaN(v)) {
            onChangeMin(Math.min(Math.max(v, min), valueMax - step))
        } else {
            setMinText(String(valueMin))
        }
    }

    const applyMax = () => {
        const v = parseFloat(maxText)
        if (!isNaN(v)) {
            onChangeMax(Math.max(Math.min(v, max), valueMin + step))
        } else {
            setMaxText(String(valueMax))
        }
    }

    const range = max - min || 1
    const leftPct = ((valueMin - min) / range) * 100
    const rightPct = ((valueMax - min) / range) * 100
    const histogramBuckets = useMemo(() => {
        const bucketCount = Math.min(36, Math.max(12, Math.ceil(Math.sqrt((histogramValues.length || 0) + (histogramOverlayValues.length || 0) || 1))))
        const countsOverall = Array.from({ length: bucketCount }, () => 0)
        const countsOverlay = Array.from({ length: bucketCount }, () => 0)
        const boundaries = Array.from({ length: bucketCount + 1 }, (_, index) => {
            const ratio = index / bucketCount
            if (histogramScale === 'log') {
                return min + Math.expm1(Math.log1p(range) * ratio)
            }
            return min + ratio * range
        })

        const bucketIndexFor = (value: number) =>
            Math.min(
                bucketCount - 1,
                Math.max(0, boundaries.findIndex((boundary, i) => i > 0 && value <= boundary) - 1)
            )

        histogramValues.forEach((raw) => {
            const value = raw || 0
            if (value < min || value > max) return
            countsOverall[bucketIndexFor(value)] += 1
        })
        histogramOverlayValues.forEach((raw) => {
            const value = raw || 0
            if (value < min || value > max) return
            countsOverlay[bucketIndexFor(value)] += 1
        })

        const highest = Math.max(...countsOverall, ...countsOverlay, 1)
        const leftRatio = (valueMin - min) / range
        const rightRatio = (valueMax - min) / range
        const heightPct = (count: number) => (count > 0 ? (Math.log1p(count) / Math.log1p(highest)) * 100 : 0)

        return countsOverall.map((count, index) => {
            const overallScaled = heightPct(count)
            const overlayScaled = heightPct(countsOverlay[index])
            const barLeft = index / bucketCount
            const barRight = (index + 1) / bucketCount
            return {
                heightOverall: count > 0 ? Math.max(18, overallScaled) : 0,
                heightOverlay: countsOverlay[index] > 0 ? Math.max(18, overlayScaled) : 0,
                left: barLeft * 100,
                width: (1 / bucketCount) * 100,
                active: barRight > leftRatio && barLeft < rightRatio,
            }
        })
    }, [histogramValues, histogramOverlayValues, histogramScale, min, max, range, valueMin, valueMax])

    return (
        <div className={styles.dualSliderWrap}>
            <div className={styles.dualSliderTrackWrap}>
                <div className={styles.dualSliderHistogram} aria-hidden="true">
                    {histogramBuckets.map((bucket, index) => (
                        <span
                            key={index}
                            className={styles.dualSliderHistogramBarWrap}
                            style={{ left: `${bucket.left}%`, width: `calc(${bucket.width}% - 2px)` }}
                        >
                            <span
                                className={styles.dualSliderHistogramBarOverall}
                                style={{ height: `${bucket.heightOverall}%` }}
                            />
                            <span
                                className={`${styles.dualSliderHistogramBarOverlay} ${bucket.active ? styles.dualSliderHistogramBarOverlayActive : ''}`}
                                style={{ height: `${bucket.heightOverlay}%` }}
                            />
                        </span>
                    ))}
                </div>
                <div className={styles.dualSliderTrack}>
                    <div
                        className={styles.dualSliderFill}
                        style={{ left: `${leftPct}%`, width: `${rightPct - leftPct}%` }}
                    />
                </div>
                <input
                    type="range"
                    className={styles.dualSliderInput}
                    min={min}
                    max={max}
                    step={step}
                    value={valueMin}
                    onChange={(e) => onChangeMin(Math.min(Number(e.target.value), valueMax - step))}
                />
                <input
                    type="range"
                    className={styles.dualSliderInput}
                    min={min}
                    max={max}
                    step={step}
                    value={valueMax}
                    onChange={(e) => onChangeMax(Math.max(Number(e.target.value), valueMin + step))}
                />
            </div>
            <div className={styles.dualSliderValues}>
                <label className={styles.dualSliderValueField}>
                    <span>Minimum</span>
                    <input
                        type="number"
                        className={styles.dualSliderTextInput}
                        value={minText}
                        onChange={(e) => setMinText(e.target.value)}
                        onBlur={applyMin}
                        onKeyDown={(e) => e.key === 'Enter' && applyMin()}
                    />
                </label>
                <label className={styles.dualSliderValueField}>
                    <span>Maximum</span>
                    <input
                        type="number"
                        className={styles.dualSliderTextInput}
                        value={maxText}
                        onChange={(e) => setMaxText(e.target.value)}
                        onBlur={applyMax}
                        onKeyDown={(e) => e.key === 'Enter' && applyMax()}
                    />
                </label>
            </div>
        </div>
    )
}
